/**
 * EmailValidationService
 *
 * Comprehensive email validation service that prevents invalid email addresses
 * during checkout and user registration, reducing bounce rates and improving
 * customer experience.
 *
 * Features:
 * - RFC 5322 compliant email format validation
 * - Domain MX record verification (DNS lookup)
 * - Typo detection with smart corrections
 * - Disposable email detection and blocking
 * - Role account detection and warnings
 * - Free provider identification
 * - Multi-environment support (browser and Node.js)
 * - Intelligent caching with TTL
 *
 * @example
 * import { emailValidationService } from "@shared/services";
 *
 * const result = await emailValidationService.validateEmail("user@gmial.com");
 * if (!result.isValid) {
 *   console.error(result.errors);
 * }
 * if (result.suggestion) {
 *   console.log(`Did you mean: ${result.suggestion}?`);
 * }
 */

import emailProviders from "../data/email-providers.json" with { type: "json" };
import disposableDomains from "../data/disposable-email-domains.json" with { type: "json" };

// Get Supabase URL and anon key from Vite environment (available in browser builds)
const SUPABASE_URL =
  typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env.VITE_SUPABASE_URL
    : null;

const SUPABASE_ANON_KEY =
  typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env.VITE_SUPABASE_ANON_KEY
    : null;

// Levenshtein distance for fuzzy matching
function levenshteinDistance(str1, str2) {
  const matrix = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1, // deletion
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}

/**
 * ValidationResult structure
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Overall validation status
 * @property {string} email - Normalized email (lowercase, trimmed)
 * @property {string[]} errors - Blocking validation errors
 * @property {string[]} warnings - Non-blocking warnings
 * @property {string} [suggestion] - Typo correction suggestion
 * @property {"high"|"medium"|"low"} confidence - Overall confidence level
 * @property {Object} details - Detailed validation results
 * @property {boolean} details.syntaxValid - Email syntax is valid
 * @property {boolean} details.domainExists - Domain has MX records
 * @property {boolean} details.hasMX - Domain has mail exchange records
 * @property {boolean} details.isDisposable - Email is from disposable provider
 * @property {boolean} details.isRole - Email is a role account
 * @property {boolean} details.isFreeProvider - Email is from free provider
 * @property {string} [details.provider] - Email provider name (e.g., "Gmail")
 */

class EmailValidationService {
  constructor() {
    // In-memory cache for MX lookups
    this.mxCache = new Map();
    this.CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    // Request deduplication - prevents duplicate in-flight requests
    this.pendingRequests = new Map();

    // Validation result caching - caches complete validation results
    this.validationCache = new Map();
    this.VALIDATION_CACHE_TTL = 60 * 1000; // 1 minute

    // Build lookup maps for fast access
    this.disposableSet = new Set(disposableDomains.domains);
    this.roleAccountSet = new Set(emailProviders.roleAccounts);

    // Build provider lookup map
    this.providerMap = new Map();
    this.typoMap = new Map();

    emailProviders.providers.forEach((provider) => {
      provider.domains.forEach((domain) => {
        this.providerMap.set(domain, {
          name: provider.name,
          category: provider.category,
        });
      });

      provider.commonTypos.forEach((typo) => {
        if (!this.typoMap.has(typo)) {
          this.typoMap.set(typo, []);
        }
        this.typoMap.get(typo).push({
          correct: provider.domains[0],
          provider: provider.name,
        });
      });
    });
  }

  /**
   * Quick syntax-only validation (synchronous)
   * @param {string} email - Email address to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateSyntax(email) {
    if (!email || typeof email !== "string") {
      return {
        isValid: false,
        errors: ["Email is required"],
      };
    }

    const normalizedEmail = email.toLowerCase().trim();
    const errors = [];

    // Check for empty string after trimming
    if (normalizedEmail.length === 0) {
      return {
        isValid: false,
        errors: ["Email is required"],
      };
    }

    // RFC 5322 compliant email regex (requires at least one dot in domain for TLD)
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

    if (!emailRegex.test(normalizedEmail)) {
      errors.push("Please enter a valid email address");
    }

    if (normalizedEmail.length > 254) {
      errors.push("Email address is too long");
    }

    if (normalizedEmail.includes("..")) {
      errors.push("Email address contains invalid consecutive dots");
    }

    const parts = normalizedEmail.split("@");
    if (parts.length !== 2) {
      errors.push("Email must contain exactly one @ symbol");
    } else {
      const [localPart, domain] = parts;

      if (localPart.length === 0) {
        errors.push("Email address cannot start with @");
      }

      if (localPart.length > 64) {
        errors.push("Email local part is too long");
      }

      if (domain.length === 0) {
        errors.push("Email domain is required");
      }

      if (domain.startsWith(".") || domain.endsWith(".")) {
        errors.push("Domain cannot start or end with a dot");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Suggest typo corrections for email addresses
   * @param {string} email - Email address to check
   * @returns {string|null} Suggested correction or null
   */
  suggestCorrection(email) {
    const normalizedEmail = email.toLowerCase().trim();
    const parts = normalizedEmail.split("@");

    if (parts.length !== 2) {
      return null;
    }

    const [localPart, domain] = parts;

    // Check if domain is already correct (known provider)
    if (this.providerMap.has(domain)) {
      return null; // Domain is correct, no suggestion needed
    }

    // Check exact typo matches
    if (this.typoMap.has(domain)) {
      const suggestions = this.typoMap.get(domain);
      return `${localPart}@${suggestions[0].correct}`;
    }

    // Check TLD typos
    for (const [typo, correction] of Object.entries(
      emailProviders.commonTldTypos,
    )) {
      if (domain.endsWith(typo)) {
        const correctedDomain =
          domain.substring(0, domain.length - typo.length) + correction;
        return `${localPart}@${correctedDomain}`;
      }
    }

    // Fuzzy match against known providers (Levenshtein distance <= 2)
    let bestMatch = null;
    let bestDistance = 3; // Only suggest if distance is 2 or less

    for (const [knownDomain] of this.providerMap) {
      const distance = levenshteinDistance(domain, knownDomain);
      if (distance < bestDistance && distance > 0) {
        bestDistance = distance;
        bestMatch = knownDomain;
      }
    }

    if (bestMatch) {
      return `${localPart}@${bestMatch}`;
    }

    return null;
  }

  /**
   * Check if domain is from a disposable email provider
   * @param {string} domain - Email domain to check
   * @returns {boolean} True if disposable
   */
  isDisposable(domain) {
    return this.disposableSet.has(domain.toLowerCase());
  }

  /**
   * Check if email is a role account
   * @param {string} email - Email address to check
   * @returns {boolean} True if role account
   */
  isRoleAccount(email) {
    const localPart = email.split("@")[0]?.toLowerCase();
    if (!localPart) return false;

    return this.roleAccountSet.has(localPart);
  }

  /**
   * Get provider information for domain
   * @param {string} domain - Email domain
   * @returns {Object|null} Provider info or null
   */
  getProviderInfo(domain) {
    return this.providerMap.get(domain.toLowerCase()) || null;
  }

  /**
   * Check domain MX records via DNS (environment-aware)
   * @param {string} domain - Domain to check
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Promise<Object>} MX check result
   */
  async checkDomainMX(domain, timeout = 3000) {
    const normalizedDomain = domain.toLowerCase();

    // Check cache first
    const cached = this.mxCache.get(normalizedDomain);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return {
        hasMX: cached.hasMX,
        cached: true,
      };
    }

    // Also check localStorage cache in browser
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const cacheKey = `email-mx-${normalizedDomain}`;
        const cachedStr = localStorage.getItem(cacheKey);
        if (cachedStr) {
          const { hasMX, timestamp } = JSON.parse(cachedStr);
          if (Date.now() - timestamp < this.CACHE_TTL) {
            return { hasMX, cached: true };
          }
        }
      } catch (_error) {
        // Ignore localStorage errors
      }
    }

    try {
      let result;

      // Detect environment and use appropriate method
      if (typeof window !== "undefined") {
        // Browser environment - use edge function
        result = await this.checkDomainMX_Browser(normalizedDomain, timeout);
      } else if (
        typeof globalThis.process !== "undefined" &&
        typeof globalThis.process.versions !== "undefined" &&
        globalThis.process.versions?.node
      ) {
        // Node.js environment - use direct DNS lookup
        result = await this.checkDomainMX_Node(normalizedDomain, timeout);
      } else {
        // Unknown environment - use fail-safe logic
        const isKnownProvider = this.providerMap.has(normalizedDomain);
        console.warn(
          `Unknown environment for MX check: ${normalizedDomain} (known provider: ${isKnownProvider})`,
        );
        return {
          hasMX: isKnownProvider,
          cached: false,
          degraded: true,
          error: "Unknown environment - cannot verify MX records",
        };
      }

      // Cache the result
      this.mxCache.set(normalizedDomain, {
        hasMX: result.hasMX,
        timestamp: Date.now(),
      });

      // Also cache in localStorage for browser
      if (typeof window !== "undefined" && window.localStorage) {
        try {
          const cacheKey = `email-mx-${normalizedDomain}`;
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              hasMX: result.hasMX,
              timestamp: Date.now(),
            }),
          );
        } catch (_error) {
          // Ignore localStorage errors
        }
      }

      return { hasMX: result.hasMX, cached: false };
    } catch (_error) {
      // Fail-safe degradation - only accept known providers on errors
      console.warn(`MX check failed for ${normalizedDomain}:`, _error.message);

      // Check if domain is a known email provider (gmail.com, outlook.com, etc.)
      const isKnownProvider = this.providerMap.has(normalizedDomain);

      if (isKnownProvider) {
        // Known provider - safe to assume has MX even though check failed
        return {
          hasMX: true,
          cached: false,
          degraded: true,
          error: _error.message,
        };
      } else {
        // Unknown domain + failed check = fail-safe (reject)
        return {
          hasMX: false,
          cached: false,
          degraded: true,
          error: _error.message,
        };
      }
    }
  }

  /**
   * Browser-based MX check using edge function
   * @private
   */
  async checkDomainMX_Browser(domain, timeout) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Use Supabase URL from Vite environment (imported at module level)
      if (!SUPABASE_URL) {
        console.warn(
          "EmailValidationService: VITE_SUPABASE_URL not configured - MX validation disabled",
        );
        throw new Error("Supabase URL not configured");
      }

      // Check for anon key (required for Edge Function authentication)
      if (!SUPABASE_ANON_KEY) {
        console.warn(
          "EmailValidationService: VITE_SUPABASE_ANON_KEY not configured - MX validation disabled",
        );
        throw new Error("Supabase anon key not configured");
      }

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/validate-email-domain`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ domain }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return { hasMX: data.hasMX };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        throw new Error("DNS lookup timeout");
      }
      throw error;
    }
  }

  /**
   * Node.js-based MX check using DNS module
   * @private
   */
  async checkDomainMX_Node(domain, timeout) {
    // Dynamic import for Node.js environment only
    const dns = await import("dns");
    const { promisify } = await import("util");
    const resolveMx = promisify(dns.resolveMx);

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("DNS lookup timeout")), timeout);
    });

    try {
      const records = await Promise.race([resolveMx(domain), timeoutPromise]);

      return { hasMX: records && records.length > 0 };
    } catch (_error) {
      // DNS errors mean no MX records
      return { hasMX: false };
    }
  }

  /**
   * Comprehensive email validation with all checks
   * @param {string} email - Email address to validate
   * @param {Object} options - Validation options
   * @param {boolean} [options.checkMX=true] - Perform DNS MX record lookup
   * @param {boolean} [options.checkTypos=true] - Suggest typo corrections
   * @param {boolean} [options.blockDisposable=true] - Block disposable emails
   * @param {boolean} [options.warnRole=true] - Warn about role accounts
   * @param {number} [options.timeout=3000] - DNS lookup timeout in ms
   * @returns {Promise<ValidationResult>} Comprehensive validation result
   */
  async validateEmail(email, options = {}) {
    const normalizedEmail = email.toLowerCase().trim();
    const cacheKey = `${normalizedEmail}-${JSON.stringify(options)}`;

    // Check validation cache first
    const cached = this.validationCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.VALIDATION_CACHE_TTL) {
      return cached.result;
    }

    // Check for pending request (deduplication)
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new validation promise
    const validationPromise = this._performValidation(email, options);

    // Store pending request
    this.pendingRequests.set(cacheKey, validationPromise);

    // Clean up after completion and cache result
    validationPromise
      .then((result) => {
        // Cache successful validation
        this.validationCache.set(cacheKey, {
          result,
          timestamp: Date.now(),
        });
      })
      .catch((error) => {
        // Don't cache errors
        console.warn("[EmailValidation] Validation error:", error);
      })
      .finally(() => {
        // Remove from pending requests
        this.pendingRequests.delete(cacheKey);
      });

    return validationPromise;
  }

  /**
   * Internal validation implementation (called by validateEmail)
   * @private
   * @param {string} email - Email address to validate
   * @param {Object} options - Validation options
   * @returns {Promise<ValidationResult>} Comprehensive validation result
   */
  async _performValidation(email, options = {}) {
    const {
      checkMX = true,
      checkTypos = true,
      blockDisposable = true,
      warnRole = true,
      timeout = 3000,
    } = options;

    // Step 1: Syntax validation
    const syntaxResult = this.validateSyntax(email);

    if (!syntaxResult.isValid) {
      return {
        isValid: false,
        email: email.toLowerCase().trim(),
        errors: syntaxResult.errors,
        warnings: [],
        confidence: "low",
        details: {
          syntaxValid: false,
          domainExists: false,
          hasMX: false,
          isDisposable: false,
          isRole: false,
          isFreeProvider: false,
        },
      };
    }

    const normalizedEmail = email.toLowerCase().trim();
    const [_localPart, domain] = normalizedEmail.split("@");

    const errors = [];
    const warnings = [];
    let suggestion = null;
    let confidence = "high";

    // Step 2: Typo detection
    if (checkTypos) {
      suggestion = this.suggestCorrection(normalizedEmail);
      if (suggestion) {
        warnings.push(`Did you mean: ${suggestion}?`);
        confidence = "medium";
      }
    }

    // Step 3: Disposable email check
    const isDisposable = this.isDisposable(domain);
    if (blockDisposable && isDisposable) {
      errors.push("Please use a permanent email address");
      confidence = "low";
    }

    // Step 4: Role account check
    const isRole = this.isRoleAccount(normalizedEmail);
    if (warnRole && isRole) {
      warnings.push(
        "This appears to be a role account. Consider using a personal email address.",
      );
    }

    // Step 5: Provider detection
    const providerInfo = this.getProviderInfo(domain);
    const isFreeProvider = providerInfo?.category === "free";

    // Step 6: MX record check (async)
    let hasMX = true;
    let domainExists = true;

    if (checkMX && !isDisposable) {
      try {
        const mxResult = await this.checkDomainMX(domain, timeout);
        hasMX = mxResult.hasMX;
        domainExists = mxResult.hasMX;

        if (!hasMX) {
          if (mxResult.degraded) {
            // MX check failed, but domain was rejected by fail-safe logic
            errors.push(
              "Could not verify email domain - please double-check your email address",
            );
          } else {
            // MX check succeeded and domain has no MX records
            errors.push("Email domain does not have valid mail servers");
          }
          confidence = "low";
        }
      } catch (error) {
        console.warn("[EmailValidation] MX check failed:", {
          domain,
          error: error.message,
        });
        // Graceful degradation on MX check failure
        warnings.push("Could not verify email domain");
        confidence = confidence === "high" ? "medium" : confidence;
      }
    }

    const result = {
      isValid: errors.length === 0,
      email: normalizedEmail,
      errors,
      warnings,
      suggestion,
      confidence,
      details: {
        syntaxValid: true,
        domainExists,
        hasMX,
        isDisposable,
        isRole,
        isFreeProvider,
        provider: providerInfo?.name,
      },
    };

    return result;
  }

  /**
   * Clear all caches (MX cache, validation cache, and pending requests)
   * Useful for testing and debugging
   */
  clearCache() {
    this.mxCache.clear();
    this.validationCache.clear();
    this.pendingRequests.clear();

    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
          if (key.startsWith("email-mx-")) {
            localStorage.removeItem(key);
          }
        });
      } catch (_error) {
        // Ignore localStorage errors
      }
    }
  }
}

// Export singleton instance
export const emailValidationService = new EmailValidationService();
